import { useState, useEffect, useCallback } from "react";
import THEME from "../../../theme";
import { fs } from "../utils/styles";
import { finanzenApi } from "../services/finanzenApi";

const geld = (n) => n == null ? "–" : `${n.toFixed(2)} €`;

export default function BwaSchaetzung({ benutzerId }) {
  const heute = new Date();
  const [monat, setMonat] = useState(heute.getMonth() + 1);
  const [jahr, setJahr] = useState(heute.getFullYear());
  const [daten, setDaten] = useState(null);
  const [laedt, setLaedt] = useState(false);
  const [offen, setOffen] = useState(false);
  const [ustvaDatei, setUstvaDatei] = useState(null);
  const [ustvaVorschau, setUstvaVorschau] = useState(null);
  const [ustvaFehler, setUstvaFehler] = useState("");

  const laden = useCallback(() => {
    setLaedt(true);
    finanzenApi.schaetzungBwa(benutzerId, monat, jahr).then(setDaten).catch(() => setDaten(null)).finally(() => setLaedt(false));
  }, [benutzerId, monat, jahr]);

  useEffect(() => { if (offen) laden(); }, [offen, laden]);

  const ustvaDateiWaehlen = (e) => {
    setUstvaDatei(e.target.files[0] || null);
    setUstvaVorschau(null);
    setUstvaFehler("");
  };

  const ustvaVorschauLaden = async () => {
    if (!ustvaDatei) return;
    setUstvaFehler("");
    try {
      setUstvaVorschau(await finanzenApi.ustvaVorschau(benutzerId, ustvaDatei));
    } catch (e) {
      setUstvaFehler(e.message);
    }
  };

  const ustvaBestaetigen = async () => {
    if (!ustvaDatei || !ustvaVorschau) return;
    try {
      await finanzenApi.ustvaImportieren(benutzerId, ustvaDatei, !!ustvaVorschau.bereits_vorhanden);
      setUstvaDatei(null);
      setUstvaVorschau(null);
      laden();
    } catch (e) {
      setUstvaFehler(e.message);
    }
  };

  return (
    <div style={{ ...fs.karte, marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <h3 style={{ color: THEME.colors.text, margin: 0 }}>📐 Schätzungs-BWA (zwischen zwei echten Lieferungen)</h3>
        <button style={{ ...fs.btnGray, width: "auto" }} onClick={() => setOffen(o => !o)}>{offen ? "▲ einklappen" : "▼ anzeigen"}</button>
      </div>
      {offen && (
        <>
          <div style={{ background: `${THEME.colors.warning}22`, border: `1px solid ${THEME.colors.warning}`, borderRadius: 8, padding: 12, margin: "12px 0" }}>
            <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.sm, margin: 0 }}>
              ⚠ Dies ist eine grobe Schätzung aus Bankumsätzen + berechneter Abschreibung – <strong>kein Ersatz</strong> für die echte BWA vom Steuerberater.
              Rückstellungen, Umsatzsteuer-Abgrenzung und Privatentnahmen/-einlagen sind nicht berücksichtigt.
            </p>
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
            <select style={{ ...fs.input, width: "auto" }} value={monat} onChange={e => setMonat(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
            </select>
            <select style={{ ...fs.input, width: "auto" }} value={jahr} onChange={e => setJahr(Number(e.target.value))}>
              {Array.from({ length: 5 }, (_, i) => heute.getFullYear() - i).map(j => <option key={j} value={j}>{j}</option>)}
            </select>
            <button style={{ ...fs.btn, width: "auto" }} onClick={laden} disabled={laedt}>{laedt ? "Lädt…" : "Aktualisieren"}</button>
          </div>

          <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 14, marginBottom: 20 }}>
            <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.sm, fontWeight: 600, margin: "0 0 8px" }}>USt-Voranmeldung hochladen (für genaueren Netto-Umsatz)</p>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <input type="file" accept=".pdf" onChange={ustvaDateiWaehlen} style={{ color: THEME.colors.text }} />
              <button style={{ ...fs.btnGray, width: "auto" }} onClick={ustvaVorschauLaden} disabled={!ustvaDatei}>Vorschau</button>
              {ustvaVorschau && <button style={{ ...fs.btn, width: "auto", background: THEME.colors.success }} onClick={ustvaBestaetigen}>Übernehmen</button>}
            </div>
            {ustvaFehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm }}>{ustvaFehler}</p>}
            {ustvaVorschau && (
              <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginTop: 8 }}>
                {String(ustvaVorschau.monat).padStart(2, "0")}/{ustvaVorschau.jahr}: Netto-Umsatz {geld(ustvaVorschau.netto_umsatz_19)}, Umsatzsteuer {geld(ustvaVorschau.umsatzsteuer)}, Vorsteuer {geld(ustvaVorschau.vorsteuer)}, Zahllast {geld(ustvaVorschau.zahllast)}
                {ustvaVorschau.bereits_vorhanden && <> – ⚠ bereits vorhanden, "Übernehmen" ersetzt die alten Werte</>}
              </p>
            )}
          </div>

          {daten && (
            <>
              {daten.ustva && (
                <div style={{ background: `${THEME.colors.success}22`, border: `1px solid ${THEME.colors.success}`, borderRadius: 8, padding: 12, marginBottom: 16 }}>
                  <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.sm, margin: 0 }}>
                    ✓ Für diesen Monat liegt eine USt-Voranmeldung vor – Netto-Umsatz {geld(daten.ustva.netto_umsatz_19)} statt geschätzt.
                    Genauer geschätztes Ergebnis (netto): <strong>{geld(daten.ergebnis_netto_geschaetzt)}</strong>
                  </p>
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 20 }}>
                <div style={{ ...fs.karte, marginBottom: 0 }}>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 6px" }}>Einnahmen (betrieblich)</p>
                  <p style={{ color: THEME.colors.success, fontSize: THEME.fontSize.xxl, fontWeight: 700, margin: 0 }}>{geld(daten.einnahmen_betrieblich)}</p>
                </div>
                <div style={{ ...fs.karte, marginBottom: 0 }}>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 6px" }}>Ausgaben (betrieblich)</p>
                  <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.xxl, fontWeight: 700, margin: 0 }}>{geld(daten.ausgaben_betrieblich)}</p>
                </div>
                <div style={{ ...fs.karte, marginBottom: 0 }}>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 6px" }}>Abschreibung (berechnet)</p>
                  <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xxl, fontWeight: 700, margin: 0 }}>{geld(-daten.abschreibung.gesamt)}</p>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "4px 0 0" }}>{daten.abschreibung.anzahl_aktive_anlagegueter} aktive Anlagegüter</p>
                </div>
                <div style={{ ...fs.karte, marginBottom: 0 }}>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 6px" }}>Ergebnis nach Abschreibung</p>
                  <p style={{ color: daten.ergebnis_nach_abschreibung >= 0 ? THEME.colors.success : THEME.colors.danger, fontSize: THEME.fontSize.xxl, fontWeight: 700, margin: 0 }}>{geld(daten.ergebnis_nach_abschreibung)}</p>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xs, margin: "4px 0 0" }}>vor Abschreibung: {geld(daten.ergebnis_vor_abschreibung)}</p>
                </div>
              </div>

              {daten.bereinigungen && (daten.bereinigungen.steuern_ausgeschlossen > 0 || daten.bereinigungen.tilgung_ausgeschlossen > 0) && (
                <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>
                  ℹ Nicht als Ausgabe gezählt: {daten.bereinigungen.steuern_ausgeschlossen > 0 && <>Steuerzahlungen {geld(daten.bereinigungen.steuern_ausgeschlossen)}</>}
                  {daten.bereinigungen.steuern_ausgeschlossen > 0 && daten.bereinigungen.tilgung_ausgeschlossen > 0 && ", "}
                  {daten.bereinigungen.tilgung_ausgeschlossen > 0 && <>Darlehenstilgung {geld(daten.bereinigungen.tilgung_ausgeschlossen)}</>}
                  .
                </p>
              )}

              {daten.nicht_kategorisierte_anzahl > 0 && (
                <p style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.sm }}>
                  ⚠ {daten.nicht_kategorisierte_anzahl} Bankumsätze in diesem Monat sind noch nicht kategorisiert – die Schätzung ist dadurch unvollständig.
                </p>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <div>
                  <h4 style={{ color: THEME.colors.text }}>Einnahmen nach Kategorie</h4>
                  {daten.einnahmen_nach_kategorie.map(k => (
                    <div key={k.kategorie} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderTop: `1px solid ${THEME.colors.border}`, fontSize: THEME.fontSize.sm }}>
                      <span style={{ color: THEME.colors.textSecondary }}>{k.kategorie}</span>
                      <span style={{ color: THEME.colors.text }}>{geld(k.betrag)}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <h4 style={{ color: THEME.colors.text }}>Ausgaben nach Kategorie</h4>
                  {daten.ausgaben_nach_kategorie.map(k => (
                    <div key={k.kategorie} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderTop: `1px solid ${THEME.colors.border}`, fontSize: THEME.fontSize.sm }}>
                      <span style={{ color: THEME.colors.textSecondary }}>{k.kategorie}</span>
                      <span style={{ color: THEME.colors.text }}>{geld(k.betrag)}</span>
                    </div>
                  ))}
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderTop: `1px solid ${THEME.colors.border}`, fontSize: THEME.fontSize.sm }}>
                    <span style={{ color: THEME.colors.textSecondary }}>Abschreibung (berechnet)</span>
                    <span style={{ color: THEME.colors.text }}>{geld(-daten.abschreibung.gesamt)}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
